import { ToggleButton, ToggleButtonGroup } from "@mui/material"
import { getSubtleSurfaceShadow } from "components/common/elevation"

export type ViewModeOption<T extends string> = {
  label: string
  value: T
}

type Props<T extends string> = {
  ariaLabel: string
  onChange: (value: T) => void
  options: readonly ViewModeOption<T>[]
  value: T
}

const ViewModeToggle = <T extends string>({ ariaLabel, onChange, options, value }: Props<T>) => {
  const selectedIndex = Math.max(
    options.findIndex((option) => option.value === value),
    0,
  )

  return (
    <ToggleButtonGroup
      aria-label={ariaLabel}
      color="primary"
      exclusive
      onChange={(_, nextValue: null | T) => {
        if (nextValue) onChange(nextValue)
      }}
      size="small"
      sx={(theme) => {
        const optionGap = 0.5
        const indicatorOffset = selectedIndex * optionGap
        const indicatorReservedSpace = 1 + (options.length - 1) * optionGap

        return {
          "@media (prefers-reduced-motion: reduce)": {
            "& .MuiToggleButtonGroup-grouped, &::before": {
              transition: "none",
            },
          },
          "& .MuiToggleButtonGroup-grouped": {
            bgcolor: "transparent",
            border: 0,
            borderRadius: 1,
            fontWeight: theme.typography.fontWeightBold,
            minWidth: 0,
            position: "relative",
            px: 2,
            textTransform: "none",
            transition: theme.transitions.create("color", {
              duration: theme.transitions.duration.shorter,
            }),
            whiteSpace: "nowrap",
            zIndex: 1,
          },
          "& .MuiToggleButtonGroup-grouped:hover": {
            bgcolor: "action.selected",
          },
          "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            borderLeft: 0,
            marginLeft: 0,
          },
          "& .MuiToggleButtonGroup-grouped.Mui-selected": {
            bgcolor: "transparent",
          },
          "& .MuiToggleButtonGroup-grouped.Mui-selected:hover": {
            bgcolor: "transparent",
          },
          "&::before": {
            bgcolor: "background.paper",
            border: 0,
            borderRadius: 1,
            bottom: theme.spacing(0.5),
            boxShadow: getSubtleSurfaceShadow(theme),
            content: '""',
            left: theme.spacing(0.5),
            pointerEvents: "none",
            position: "absolute",
            top: theme.spacing(0.5),
            transform: `translateX(calc(${selectedIndex * 100}% + ${theme.spacing(indicatorOffset)}))`,
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shorter,
              easing: theme.transitions.easing.easeInOut,
            }),
            width: `calc((100% - ${theme.spacing(indicatorReservedSpace)}) / ${options.length})`,
          },
          bgcolor: "action.hover",
          borderRadius: 1.5,
          display: "inline-grid",
          flexShrink: 0,
          gap: optionGap,
          gridAutoColumns: "1fr",
          gridAutoFlow: "column",
          p: 0.5,
          position: "relative",
        }
      }}
      value={value}
    >
      {options.map((option) => (
        <ToggleButton aria-label={option.label} key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

export default ViewModeToggle
